import { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown, Calendar, Video, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import PhoneSwap from '@/components/PhoneSwap';

// Twitter/X icon (lucide doesn't ship official X icon; render as text "𝕏")
function XIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z"/>
    </svg>
  );
}

interface NavLink {
  label: string;
  href: string;
  children?: NavChild[];
}

interface NavChild {
  label: string;
  href: string;
  photo?: string; // webp path for attorney dropdown
  photoFallback?: string;
}

const practiceAreas: NavChild[] = [
  { label: 'Estate Planning', href: '/estate-planning/' },
  { label: 'Probate', href: '/probate/' },
  { label: 'Wills', href: '/wills/' },
  { label: 'Trusts', href: '/trusts/' },
  { label: 'Wills & Trusts', href: '/wills-and-trusts/' },
  { label: 'Asset Protection', href: '/asset-protection/' },
  { label: 'Guardianship', href: '/guardianship/' },
  { label: 'Power of Attorney', href: '/power-of-attorney/' },
  { label: 'Elder Law', href: '/elder-law/' },
  { label: 'Family Law', href: '/family-law/' },
  { label: 'Matrimonial', href: '/matrimonial/' },
  { label: 'Real Estate', href: '/real-estate/' },
  { label: 'Personal Injury', href: '/personal-injury/' },
  { label: 'Medicaid Planning', href: '/medicaid-planning/' },
  { label: 'Civil Rights', href: '/civil-rights/' },
];


const contactMenu: NavChild[] = [
  { label: 'Contact Us', href: '/contact-us/' },
  { label: 'Locations', href: '/location/' },
];

const aboutUs: NavChild[] = [
  { label: 'About the Firm', href: '/about-us/' },
  { label: 'Our Attorneys', href: '/attorneys/' },
  // «Client Reviews» убран с верхнего уровня — меню было длиннее, чем помещается
  // без переноса. Ссылка остаётся общесайтовой: выпадающее меню печатается
  // в разметке каждой страницы, достижимость /reviews/ не меняется.
  { label: 'Client Reviews', href: '/reviews/' },
];

const mediaNews: NavChild[] = [
  { label: 'Blog', href: '/blog/' },
  { label: 'Publications', href: '/publications/' },
  { label: 'Podcast — Obsessed With Success', href: '/obsessed-with-success/' },
  { label: 'YouTube Channel', href: 'https://www.youtube.com/@MorganLegalGroup' },
  { label: 'News & Press', href: '/publications/' },
];

const navLinks: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Practice Areas', href: '/practice-areas/', children: practiceAreas },
  { label: 'Media & News Coverage', href: '/publications/', children: mediaNews },
  // Как на morganlegalgroup: список из пятнадцати имён в меню не разворачивается,
  // «Our Attorneys» — одна ссылка на реестр внутри «About Us». Реестр и есть
  // точка входа к личным страницам.
  { label: 'About Us', href: '/about-us/', children: aboutUs },
  // Locations переехали под Contact — как «Our Attorneys» под «About Us».
  // Ссылка на /location/ остаётся общесайтовой, только этажом ниже.
  { label: 'Contact', href: '/contact-us/', children: contactMenu },
];

// Social handles — sourced from existing SchemaJsonLd Organization sameAs;
// remaining links use the firm's standard handles. TODO: confirm with marketing.
const socials = [
  { label: 'Facebook', href: 'https://www.facebook.com/morganlegalpc', Icon: Facebook },
  { label: 'Instagram', href: 'https://www.instagram.com/morganlegalpc/', Icon: Instagram },
  { label: 'X (Twitter)', href: 'https://twitter.com/morganlegalpc', Icon: XIcon },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/morgan-legal-group', Icon: Linkedin },
  { label: 'YouTube', href: 'https://www.youtube.com/@MorganLegalGroup', Icon: Youtube },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-navy shadow-2xl border-b-2 border-gold' : 'bg-navy/95 backdrop-blur-sm'
      }`}
    >
      {/* Utility strip: socials + video conf + schedule + phone */}
      <div className="hidden lg:block border-b border-white/10 bg-navy/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-9 text-xs">
            <div className="flex items-center gap-3 text-white/55">
              <span>15 Maiden Lane, Suite 905 · NYC</span>
              <span className="text-gold/40">·</span>
              <span>Mon–Fri 9:00–18:00 ET</span>
            </div>
            <div className="flex items-center gap-1">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Morgan Legal Group on ${label}`}
                  className="w-7 h-7 flex items-center justify-center text-white/55 hover:text-gold transition-colors"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
              <span className="mx-2 h-4 w-px bg-white/15" />
              <a
                href="/appointment/"
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-gold transition-colors px-2 py-1"
              >
                <Video className="w-3.5 h-3.5" />
                <span>Video Consultation</span>
              </a>
              <a
                href="/appointment/"
                className="inline-flex items-center gap-1.5 text-gold hover:text-gold-light transition-colors px-2 py-1 font-semibold"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>Schedule Your Consultation</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a href="/" className="flex items-center gap-3 group flex-shrink-0" aria-label="Morgan Legal Group — Home">
            <picture>
              <source srcSet="/brand/morgan-logo.webp" type="image/webp" />
              <img
                src="/brand/morgan-logo.png"
                alt="Morgan Legal Group · Attorneys at Law"
                width="143"
                height="52"
                className="h-10 lg:h-12 w-auto opacity-90 group-hover:opacity-100 transition-opacity duration-200"
              />
            </picture>
          </a>

          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(link.label)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <a
                    href={link.href}
                    className="flex items-center gap-1 px-2.5 py-2 text-white/80 hover:text-gold font-body text-sm font-medium whitespace-nowrap transition-colors duration-200"
                  >
                    {link.label}
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                  </a>
                  <div
                    className={`absolute top-full left-0 mt-1 bg-navy border border-gold/30 shadow-2xl transition-all duration-200 ${
                      link.label === 'Our Attorneys' ? 'w-80' : 'w-60'
                    } ${
                      openMenu === link.label
                        ? 'opacity-100 translate-y-0 pointer-events-auto'
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    {link.children.map((child) => (
                      <a
                        key={child.label + child.href}
                        href={child.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-gold hover:bg-white/5 font-body text-sm transition-colors duration-150 border-b border-white/5 last:border-0"
                      >
                        {child.photo ? (
                          <picture className="flex-shrink-0">
                            <source srcSet={child.photo} type="image/webp" />
                            <img
                              src={child.photoFallback}
                              alt=""
                              width="40"
                              height="40"
                              loading="lazy"
                              className="w-8 h-8 rounded-full object-cover border border-gold/40"
                            />
                          </picture>
                        ) : null}
                        <span>{child.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="px-2.5 py-2 text-white/80 hover:text-gold font-body text-sm font-medium whitespace-nowrap transition-colors duration-200"
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <a
              href="tel:8885291315"
              className="flex items-center gap-2 text-gold hover:text-gold-light font-body text-sm font-medium whitespace-nowrap transition-colors duration-200"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <PhoneSwap />
            </a>
            <a href="/appointment/" className="btn-gold text-sm py-2.5 px-4 whitespace-nowrap">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              Schedule Your Consultation
            </a>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-navy border-t border-gold/30 px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <details key={link.label} className="group border-b border-white/5">
              <summary className="flex items-center justify-between px-3 py-3 text-white/80 group-open:text-gold font-body text-base font-medium list-none cursor-pointer">
                <a href={link.href} className="flex-1">{link.label}</a>
                {link.children ? <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" /> : null}
              </summary>
              {link.children ? (
                <div className="pb-2">
                  {link.children.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      className="block px-6 py-2 text-white/65 hover:text-gold font-body text-sm transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </details>
          ))}
          <div className="pt-3 space-y-3">
            <div className="flex items-center gap-2 px-3">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Morgan Legal Group on ${label}`}
                  className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <a href="tel:8885291315" className="flex items-center gap-2 text-gold font-body text-base font-medium px-3">
              <Phone className="w-4 h-4" />
              <PhoneSwap />
            </a>
            <a href="/appointment/" className="btn-gold w-full justify-center text-sm">
              <Calendar className="w-4 h-4" />
              Schedule Your Consultation
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
